/**
 * Mine | My team | All — the one scope switch every Home panel uses (Bill 2026-10-16: "Extend the Mine/My Team toggle
 * to both cart panels as well and remove the see all pilot checkbox", then "Mine/My Team/All as options in the toggle").
 *
 * The server decides which options a person gets (GET v3/team/views, from their sign-in and server roles):
 *   My team — Supervisors: the groups they supervise; Managers: the branches in their region. Shown only once that
 *             team exists (groups come from Entra direct reports and the admin's subordinate overrides).
 *   All     — every officer's work. Admins and Executives always; Supervisors and Managers unless the admin setting
 *             retail.team.scopeToGroupsAndRegions is on.
 * My team is for Supervisors and Managers only. Executives look but don't reassign. Officers see no switch. Each panel remembers its own choice for the session.
 */
import { useEffect, useState } from 'react';
import { Globe, User, Users } from 'lucide-react';
import type { TeamViewsResponse } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';

export type ScopeMode = 'mine' | 'team' | 'all';

const TEAM_ROLES = ['supervisor', 'manager', 'admin', 'superadmin', 'executive'];

export interface ScopeViews {
  team: boolean;
  all: boolean;
  /** May move tasks (Supervisor / Manager / Admin). */
  canReassign: boolean;
  /** Short description of "My team" for its tooltip. */
  teamBasis: string;
}

const NONE: ScopeViews = { team: false, all: false, canReassign: false, teamBasis: '' };

// One request per session for all three panels.
let cached: Promise<ScopeViews> | null = null;

function fetchViews(get: () => Promise<TeamViewsResponse>): Promise<ScopeViews> {
  if (!cached) {
    cached = get()
      .then((r) => ({ team: !!r.team?.available, all: !!r.all?.available, canReassign: !!r.canReassign, teamBasis: r.team?.basis ?? '' }))
      .catch(() => {
        cached = null; // try again next time
        return NONE;
      });
  }
  return cached;
}

/** Which scope options this person gets. Officers never call the server. */
export function useScopeViews(): ScopeViews {
  const { user } = useAuth();
  const client = useApiClient();
  const eligible = (user?.roles ?? []).some((r) => TEAM_ROLES.includes(String(r).toLowerCase()));
  const [views, setViews] = useState<ScopeViews>(NONE);
  useEffect(() => {
    if (!eligible) { setViews(NONE); return undefined; }
    let live = true;
    void fetchViews(() => client.retail.followUpTasks.teamViews()).then((v) => { if (live) setViews(v); });
    return () => { live = false; };
  }, [client, eligible]);
  return views;
}

function read(key: string): ScopeMode | null {
  try {
    const v = sessionStorage.getItem(key);
    return v === 'mine' || v === 'team' || v === 'all' ? v : null;
  } catch {
    return null;
  }
}

/**
 * A panel's scope, remembered for the session under `storageKey`. A remembered option the person no longer has
 * (for example All after an admin turns the setting on) falls back to Mine.
 */
export function useScopeMode(storageKey: string, views: ScopeViews): [ScopeMode, (m: ScopeMode) => void] {
  const [mode, setMode] = useState<ScopeMode>(() => read(storageKey) ?? 'mine');
  const effective: ScopeMode = (mode === 'team' && !views.team) || (mode === 'all' && !views.all) ? 'mine' : mode;
  const set = (m: ScopeMode): void => {
    setMode(m);
    try { sessionStorage.setItem(storageKey, m); } catch { /* per-viewer convenience only */ }
  };
  return [effective, set];
}

/** Title for a panel in each scope, e.g. { mine: 'My Carts', team: 'Team Carts', all: 'All Carts' }. */
export type ScopeTitles = Record<ScopeMode, string>;

export function ScopeSwitch({ mode, views, onChange, label }: {
  mode: ScopeMode;
  views: ScopeViews;
  onChange: (m: ScopeMode) => void;
  /** What the panel lists, for screen readers: "tasks", "carts", "online applications". */
  label: string;
}): JSX.Element | null {
  if (!views.team && !views.all) return null;
  const opts: Array<{ m: ScopeMode; text: string; icon: JSX.Element; title: string }> = [
    { m: 'mine', text: 'Mine', icon: <User size={13} aria-hidden />, title: `Your own ${label}` },
  ];
  if (views.team) {
    opts.push({
      m: 'team', text: 'My team', icon: <Users size={13} aria-hidden />,
      title: `${label[0]!.toUpperCase()}${label.slice(1)} for your team (${views.teamBasis || 'your groups or region'})`,
    });
  }
  if (views.all) {
    opts.push({ m: 'all', text: 'All', icon: <Globe size={13} aria-hidden />, title: `Everyone's ${label}, across every branch` });
  }
  return (
    <div className="home-card__scope tg-switch" role="tablist" aria-label={`Whose ${label}`}>
      {opts.map((o) => (
        <button key={o.m} type="button" role="tab" aria-selected={mode === o.m} title={o.title}
          className={`tg-switch__btn${mode === o.m ? ' is-on' : ''}`} onClick={() => onChange(o.m)}>
          {o.icon} {o.text}
        </button>
      ))}
    </div>
  );
}
